import {
  faCalendarAlt,
  faCamera,
  faChartArea,
  faChartLine,
  faCheckCircle,
  faClipboardList,
  faFish,
  faLayerGroup,
  faList,
  faMapMarkedAlt,
  faProjectDiagram,
  faSitemap,
  faSlidersH,
  faStream,
  faThermometerHalf,
  faVideo,
  faVirus,
  faWater,
  faWeightScale,
} from "@fortawesome/free-solid-svg-icons";
import {
  PermissionType,
  PermissionTypeValue,
} from "@/pages/Configuration/AdminConfiguration/Users/types/cognitoTypes";
import type { IconProps } from "@/shared/coralreef";
import type { AppIcons } from "../icon/Icon";
import { registerRoutePermission } from "./routePermissionRegistry";

export interface SidebarRoute {
  path: string;
  permission: PermissionTypeValue;
  text: string;
  collapsedText: string;
  testId: string;
  icon?: IconProps["icon"];
  svgIcon?: AppIcons;
  withSearch?: boolean;
  electronOnly?: boolean;
  appName?: string;
  children?: SidebarRoute[];
}

export type SidebarGroupKey = "plan" | "track" | "explore" | "act";

export const DASHBOARD_ROUTE_PREFIX = "/tide/dashboard";

export const SIDEBAR_ROUTES: Record<SidebarGroupKey, SidebarRoute[]> = {
  plan: [
    {
      path: "/plan",
      permission: PermissionType.Plan,
      text: "AKVA Plan",
      collapsedText: "Plan",
      icon: faClipboardList,
      appName: "@akvanext/plan",
      testId: "shell-plan-link",
      children: [
        {
          path: "/plan/planner/production-planner",
          permission: PermissionType.Plan,
          text: "Production planner",
          collapsedText: "Production planner",
          icon: faStream,
          appName: "@akvanext/plan",
          testId: "shell-plan-production-planner-link",
        },
        {
          path: "/plan/planner/smolt",
          permission: PermissionType.Plan,
          text: "Smolt",
          collapsedText: "Smolt",
          icon: faFish,
          appName: "@akvanext/plan",
          testId: "shell-plan-smolt-link",
        },
        {
          path: "/plan/planner/farm-status",
          permission: PermissionType.Plan,
          text: "Farm status",
          collapsedText: "Farm status",
          icon: faWater,
          appName: "@akvanext/plan",
          testId: "shell-plan-farm-status-link",
        },
      ],
    },
    {
      path: "/plan/planner/growth-lab",
      permission: PermissionType.Plan,
      text: "Growth models",
      collapsedText: "Growth models",
      icon: faChartLine,
      appName: "@akvanext/plan",
      testId: "shell-plan-growth-models-link",
      children: [
        {
          path: "/plan/planner/temperature-profiles",
          permission: PermissionType.Plan,
          text: "Temperature profiles",
          collapsedText: "Temperature profiles",
          icon: faThermometerHalf,
          appName: "@akvanext/plan",
          testId: "shell-plan-temperature-profiles-link",
        },
        {
          path: "/plan/planner/profiles",
          permission: PermissionType.Plan,
          text: "General profiles",
          collapsedText: "General profiles",
          icon: faLayerGroup,
          appName: "@akvanext/plan",
          testId: "shell-plan-general-profiles-link",
        },
      ],
    },
  ],
  track: [
    {
      path: "/tide/map",
      permission: PermissionType.Map,
      text: "Map",
      collapsedText: "Map",
      icon: faMapMarkedAlt,
      withSearch: true,
      testId: "sidebar-item-map",
    },
    {
      path: "/tide/goals",
      permission: PermissionType.Goals,
      text: "Goals",
      collapsedText: "Goals",
      icon: faCheckCircle,
      withSearch: true,
      testId: "sidebar-item-goals",
    },
    {
      path: "/tide/calendar",
      permission: PermissionType.Calendar,
      text: "Calendar",
      collapsedText: "Calendar",
      icon: faCalendarAlt,
      withSearch: true,
      testId: "sidebar-item-calendar",
    },
    {
      path: "/tide/biolife",
      permission: PermissionType.AlgaeDashboard,
      text: "Biolife",
      collapsedText: "Biolife",
      icon: faVirus,
      withSearch: true,
      testId: "sidebar-item-biolife",
    },
  ],
  explore: [
    {
      path: "/tide/analytics",
      permission: PermissionType.Data,
      text: "Analytics",
      collapsedText: "Data",
      icon: faChartArea,
      withSearch: true,
      testId: "sidebar-item-analytics",
    },
    {
      path: "/akva-analytics",
      permission: PermissionType.AkvaAnalytics,
      text: "Feeding Analytics",
      collapsedText: "Feeding Analytics",
      svgIcon: "fish",
      withSearch: true,
      testId: "akva-feeding-analytics-link",
    },
    {
      path: "/biomass",
      permission: PermissionType.Biomass,
      text: "Biomass",
      collapsedText: "Biomass",
      icon: faWeightScale,
      withSearch: true,
      testId: "sidebar-item-biomass",
    },
  ],
  act: [
    {
      path: "/marlin-v2/video",
      permission: PermissionType.Video,
      text: "Video",
      collapsedText: "Video",
      icon: faVideo,
      electronOnly: true,
      testId: "video-navigation-link",
    },
    {
      path: "/marlin-v2/feeding",
      permission: PermissionType.Feeding,
      text: "Feeding",
      collapsedText: "Feeding",
      icon: faList,
      electronOnly: true,
      testId: "feeding-navigation-link",
    },
    {
      path: "/marlin-v2/defaults",
      permission: PermissionType.Feeding,
      text: "Defaults",
      collapsedText: "Defaults",
      icon: faSlidersH,
      withSearch: true,
      testId: "sidebar-item-defaults",
    },
    {
      path: "/tide/feeding-strategies",
      permission: PermissionType.Feed,
      text: "Feed Automation",
      collapsedText: "Feed",
      icon: faProjectDiagram,
      withSearch: true,
      testId: "sidebar-item-feeding-strategies",
    },
    {
      path: "/tide/automation-form/select",
      permission: PermissionType.Automation,
      text: "Automation",
      collapsedText: "Automation",
      icon: faSitemap,
      withSearch: true,
      testId: "sidebar-item-automation",
    },
    {
      path: "/tide/camera-automation",
      permission: PermissionType.Camera,
      text: "Camera Automation",
      collapsedText: "Camera",
      icon: faCamera,
      withSearch: true,
      testId: "sidebar-item-camera-automation",
    },
  ],
};

export const SUBMENU_ROUTES = {
  planAudit: {
    path: "/plan/planner/audit",
    permission: PermissionType.Plan,
  },
  integrations: {
    path: "/marlin-v2/admin-configuration/equipment/integration",
    permission: PermissionType.Support,
  },
  equipment: {
    path: "/marlin-v2/admin-configuration/equipment/configuration",
    permission: PermissionType.Support,
  },
  equipmentOverview: {
    path: "/marlin-v2/admin-configuration/equipment/overview",
    permission: PermissionType.Support,
  },
  localServers: {
    path: "/marlin-v2/admin-configuration/local-servers",
    permission: PermissionType.Support,
  },
  dataRouter: {
    path: "/marlin-v2/admin-configuration/local-configuration",
    permission: PermissionType.Support,
  },
  notificationSettings: {
    path: "/marlin-v2/admin-configuration/notification-settings",
    permission: PermissionType.Support,
  },
  videoSettings: {
    path: "/marlin-v2/admin-configuration/video-settings",
    permission: PermissionType.Support,
  },
  feedingSettings: {
    path: "/marlin-v2/admin-configuration/feeding-settings",
    permission: PermissionType.Support,
  },
  adminSettings: {
    path: "/marlin-v2/admin-configuration/settings",
    permission: PermissionType.CameraCsv,
  },
  networkScanner: {
    path: "/akva-network-scanner",
    permission: PermissionType.CameraCsv,
  },
} satisfies Record<string, { path: string; permission: PermissionTypeValue }>;

Object.values(SIDEBAR_ROUTES)
  .flat()
  .forEach((r) => registerRoutePermission(r.path, r.permission));
Object.values(SUBMENU_ROUTES).forEach((r) =>
  registerRoutePermission(r.path, r.permission),
);
registerRoutePermission(DASHBOARD_ROUTE_PREFIX, PermissionType.Dashboard);
